import {
  type AuthUser,
  type Keycloak,
  resolveAuthData,
  setKeycloakInstance,
} from "@spiriidk/auth";
import type { KeycloakTokenParsed } from "keycloak-js";

import {
  DEFAULT_AUTH_PROFILE,
  buildTestUser,
  type AuthProfileSelection,
  type TestUser,
} from "./test-user";

// Far-future expiry (year 2100) so the stubbed token never looks expired
// during a run; the stub is never `init()`-ed, so keycloak-js sets no refresh
// timer and `onTokenExpired` never fires.
const TOKEN_EXPIRY_SECONDS = 4102444800;

/**
 * Base64url-encode an ASCII string (JWT segment encoding).
 */
function base64Url(input: string): string {
  return btoa(input)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll(/=+$/g, "");
}

/**
 * Build an unsigned JWT carrying the parsed claims. Nothing in the app decodes
 * this (claims are read from `tokenParsed`), but a well-formed token keeps any
 * `Authorization: Bearer <token>` header realistic.
 */
function buildToken(tokenParsed: KeycloakTokenParsed): string {
  const header = base64Url(JSON.stringify({ alg: "none", typ: "JWT" }));
  const payload = base64Url(JSON.stringify(tokenParsed));
  return `${header}.${payload}.`;
}

/**
 * Build a stubbed Keycloak instance for the {@link TEST_USER}. Typed against
 * the real `Keycloak` surface (via `satisfies Partial<Keycloak>`) so a
 * keycloak-js upgrade that changes one of the members the app relies on
 * surfaces as a compile error here.
 */
function createMockKeycloak(user: TestUser): Keycloak {
  // Typed as the base KeycloakTokenParsed: its index signature carries the
  // Spirii-specific claims (permittedOperatorId, hyphenated "resource-access")
  // that resolveAuthData reads back via its own AuthTokenParsed cast.
  const tokenParsed: KeycloakTokenParsed = {
    preferred_username: user.username,
    email: user.email,
    name: user.name,
    sub: user.userId,
    permittedOperatorId: String(user.operatorId),
    permittedCompanyId: String(user.companyId),
    permittedBetaTest: false,
    groups: [],
    exp: TOKEN_EXPIRY_SECONDS,
    iat: 0,
    "resource-access": { techportal: { roles: user.roles } },
  };

  const token = buildToken(tokenParsed);

  const keycloak = {
    authenticated: true,
    subject: user.userId,
    token,
    refreshToken: token,
    idToken: token,
    tokenParsed,
    updateToken: () => Promise.resolve(false),
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    clearToken: () => {},
    hasRealmRole: () => false,
    hasResourceRole: () => false,
  } satisfies Partial<Keycloak>;

  return keycloak as unknown as Keycloak;
}

/**
 * Inject the mock Keycloak instance through the shared `setKeycloakInstance`
 * seam (so remotes receive a working session exactly as they would from a real
 * login) and return the matching auth data, skipping every Keycloak network
 * call. Enabled only behind the mock flag by `initializeApp`.
 *
 * `profile` selects the seeded user's permission set and company/operator scope
 * (the e2e auth-scope seam — {@link ./auth-overrides}); it defaults to the
 * full-access, company-scoped user so the seam is invisible without an override.
 */
function setupMockAuth(
  profile: AuthProfileSelection = DEFAULT_AUTH_PROFILE
): AuthUser {
  const keycloak = createMockKeycloak(buildTestUser(profile));
  setKeycloakInstance(keycloak);
  return resolveAuthData(keycloak);
}

export { setupMockAuth };
